import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, UserCog, Pencil, Ban, Send, Copy, Check, KeyRound, Link2 } from "lucide-react";
import { toast } from "sonner";

const COUNTRIES = ["USA", "Canada"];
const emptyForm = {
  full_name: "", email: "", password: "", role: "", branch_ids: [],
  country: "", state: "", title: "", phone: "", status: "active",
};

export default function Users() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  // Setup-link dialog (shown after invite / resend)
  const [linkInfo, setLinkInfo] = useState(null); // { email, url }
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data.items || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.get("/branches").then((r) => setBranches(r.data.items || [])).catch(() => {});
    api.get("/meta").then((r) => setRoles(r.data.assignable_roles || [])).catch(() => {});
  }, [load]);

  const roleMeta = (value) => roles.find((r) => r.value === value);
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "";
  const availableRoles = roles.filter((r) => isSuper || r.value !== "super_admin");

  const openNew = () => {
    const def = availableRoles.find((r) => r.value === "intake_coordinator") || availableRoles[0];
    setEditing(null);
    setForm({ ...emptyForm, role: def?.value || "" });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      ...emptyForm, ...u, password: "",
      country: u.country || "", state: u.state || "", branch_ids: u.branch_ids || [],
      status: u.status || "active",
    });
    setOpen(true);
  };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleBranch = (id) => setForm((p) => ({ ...p, branch_ids: p.branch_ids.includes(id) ? p.branch_ids.filter((x) => x !== id) : [...p.branch_ids, id] }));

  const scope = roleMeta(form.role)?.scope; // global | country | state | branch | assigned | self
  const showCountry = scope === "country" || scope === "state";
  const showState = scope === "state";
  const showBranches = scope === "branch" || scope === "assigned";

  const buildUrl = (link) => `${window.location.origin}${link}`;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        const payload = {
          full_name: form.full_name, role: form.role, branch_ids: form.branch_ids,
          title: form.title, phone: form.phone, status: form.status,
          country: form.country || null, state: form.state || null,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
        setOpen(false);
        await load();
      } else {
        const payload = {
          full_name: form.full_name, email: form.email, role: form.role,
          branch_ids: form.branch_ids, title: form.title, phone: form.phone,
          country: form.country || null, state: form.state || null,
        };
        if (form.password) payload.password = form.password;
        const { data } = await api.post("/users", payload);
        setOpen(false);
        await load();
        if (data.setup_link) {
          setLinkInfo({ email: data.email, url: buildUrl(data.setup_link) });
          toast.success("Invitation created — share the setup link");
        } else {
          toast.success("User created with a password");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSubmitting(false); }
  };

  const resendInvite = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/resend-invite`);
      setLinkInfo({ email: data.email, url: buildUrl(data.setup_link) });
      toast.success("New setup link generated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resend invite"); }
  };

  const deactivate = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success("User deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const copyLink = () => {
    if (!linkInfo) return;
    navigator.clipboard.writeText(linkInfo.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const scopeSummary = (u) => {
    const lvl = u.scope_level;
    if (lvl === "global") return "All countries";
    if (lvl === "country") return u.country || "Country";
    if (lvl === "state") return [u.state, u.country].filter(Boolean).join(", ") || "State";
    if (lvl === "self" || lvl === "assigned") return u.is_portal ? "Portal" : "Assigned";
    const names = (u.branch_ids || []).map(branchName).filter(Boolean);
    return names.join(", ") || "—";
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221]">Users &amp; Access</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Invite team members, assign roles, and control geographic access.</p>
        </div>
        <Button onClick={openNew} data-testid="new-user-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>
          <Plus size={17} className="mr-1.5" /> Invite User
        </Button>
      </div>

      <div className="rounded-xl border border-[#E6EDF2] bg-white overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        {loading ? (
          <div className="p-4 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : (
          <div className="overflow-x-auto mmm-scroll">
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Name</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Email</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Role</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Access Scope</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className="hover:bg-[rgba(155,185,206,0.10)]" data-testid="user-row">
                    <TableCell className="py-3 font-semibold text-[#252221]">{u.full_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell><StatusBadge value={u.role} label={u.role_label} /></TableCell>
                    <TableCell className="text-sm text-[#252221]">{scopeSummary(u)}</TableCell>
                    <TableCell><StatusBadge value={u.status} /></TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        {u.status === "pending" && (
                          <button onClick={() => resendInvite(u)} className="p-1.5 rounded-lg text-[#1C4358] hover:bg-[rgba(28,67,88,0.08)]" title="Resend invite" data-testid="resend-invite-button"><Send size={15} /></button>
                        )}
                        <button onClick={() => openEdit(u)} className="p-1.5 rounded-lg text-[#1C4358] hover:bg-[rgba(28,67,88,0.08)]" title="Edit" data-testid="edit-user-button"><Pencil size={15} /></button>
                        {u.id !== user.id && u.status !== "inactive" && (
                          <button onClick={() => deactivate(u)} className="p-1.5 rounded-lg text-[#9C1E22] hover:bg-[rgba(156,30,34,0.08)]" title="Deactivate" data-testid="deactivate-user-button"><Ban size={15} /></button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto mmm-scroll" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><UserCog size={20} className="text-[#1C4358]" /> {editing ? "Edit User" : "Invite User"}</DialogTitle>
            <DialogDescription>{editing ? "Update this team member's role, scope, and status." : "Leave the password blank to send a secure setup invitation."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="mb-1.5 block text-[13px] font-medium">Full Name *</Label>
                <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} required data-testid="user-fullname" />
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required disabled={!!editing} data-testid="user-email" />
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Role *</Label>
                <Select value={form.role} onValueChange={(v) => set("role", v)}>
                  <SelectTrigger data-testid="user-role"><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">{availableRoles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {roleMeta(form.role)?.desc && (
              <p className="text-xs text-muted-foreground -mt-1 rounded-lg bg-[#F7FAFC] border border-[#E6EDF2] px-3 py-2">{roleMeta(form.role).desc}</p>
            )}

            {/* Geographic scope */}
            {(showCountry || showState) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {showCountry && (
                  <div>
                    <Label className="mb-1.5 block text-[13px] font-medium">Country *</Label>
                    <Select value={form.country} onValueChange={(v) => set("country", v)}>
                      <SelectTrigger data-testid="user-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                      <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {showState && (
                  <div>
                    <Label className="mb-1.5 block text-[13px] font-medium">State / Province *</Label>
                    <Input value={form.state} onChange={(e) => set("state", e.target.value)} placeholder="e.g. MO" data-testid="user-state" />
                  </div>
                )}
              </div>
            )}

            {showBranches && (
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Branch Access</Label>
                <div className="flex flex-wrap gap-2">
                  {branches.map((b) => {
                    const active = form.branch_ids.includes(b.id);
                    return (
                      <button type="button" key={b.id} onClick={() => toggleBranch(b.id)} data-testid={`user-branch-${b.code}`}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "text-white border-transparent" : "bg-white text-[#1C4358] border-[#E6EDF2] hover:bg-[rgba(155,185,206,0.12)]"}`}
                        style={active ? { backgroundColor: "var(--mmm-navy)" } : {}}>
                        {b.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Title</Label>
                <Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="user-title" />
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Phone</Label>
                <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="user-phone" />
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">{editing ? "New Password (optional)" : "Password (optional)"}</Label>
                <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder={editing ? "Leave blank to keep current" : "Leave blank to send invite"} data-testid="user-password" />
              </div>
              {editing && (
                <div>
                  <Label className="mb-1.5 block text-[13px] font-medium">Status</Label>
                  <Select value={form.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger data-testid="user-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="user-cancel-button">Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="user-submit-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>{submitting ? "Saving..." : (editing ? "Save" : "Create")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Setup link dialog */}
      <Dialog open={!!linkInfo} onOpenChange={(o) => !o && setLinkInfo(null)}>
        <DialogContent className="max-w-md" data-testid="setup-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><KeyRound size={20} className="text-[#2F7D5A]" /> Setup link ready</DialogTitle>
            <DialogDescription>Share this secure link with <span className="font-medium text-[#252221]">{linkInfo?.email}</span> so they can set their own password. No password is stored until they complete setup.</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-[#E6EDF2] bg-[#F7FAFC] p-3">
            <div className="flex items-center gap-2 text-[12px] text-muted-foreground mb-1"><Link2 size={13} /> One-time setup link</div>
            <code className="text-xs break-all text-[#1C4358]" data-testid="setup-link-value">{linkInfo?.url}</code>
          </div>
          <div className="flex gap-2">
            <Button onClick={copyLink} className="flex-1 text-white" style={{ backgroundColor: "var(--mmm-navy)" }} data-testid="copy-setup-link-button">
              {copied ? <Check size={15} className="mr-1.5" /> : <Copy size={15} className="mr-1.5" />} {copied ? "Copied" : "Copy link"}
            </Button>
            <Button variant="outline" onClick={() => setLinkInfo(null)} data-testid="setup-link-done-button">Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
